import type { ComponentProps, ImgHTMLAttributes } from 'react';
import React, { useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { ImageFormat } from 'corgi-types';
import type {
  BorderColorProps,
  BorderRadiusProps,
  BorderWidthProps,
  DisplayProps,
} from 'styled-system';
import {
  borderColor,
  borderRadius,
  borderWidth,
  display,
  backgroundColor as styledBackgroundColor,
  themeGet,
} from 'styled-system';

import { useLoadingImageBackground } from '../hooks/image';
import { getForwardPropOpts } from '../theme/helpers/forward-props';
import type { BackgroundColorProps } from '../theme/system-types';
import type { BoxSizingProps, ObjectFitProps } from '../theme/system-utilities';
import { boxSizing, objectFit } from '../theme/system-utilities';
import { breakpoints } from '../theme/theme';
import type {
  ResponsiveImageBreakpointWidths,
  ResponsiveImageSingleWidth,
  ResponsiveImageWidths,
} from '../types/Image';
import { transformImageUrl } from '../utils/cdn/image';
import { createLazyLoadingAttribute } from '../utils/image';

const FALLBACK_IMAGE_URL =
  'https://cdn1.home24.net/prismic-img/home24-production/aS19anNYClf9nobE_fallback-image.png';

interface ResponsiveImageProps
  extends
    ImgHTMLAttributes<HTMLImageElement>,
    BackgroundColorProps,
    ObjectFitProps,
    BorderRadiusProps,
    DisplayProps {
  src: string;
  widths?: ResponsiveImageWidths;
  fallbackSize?: number;
  alt?: string;
  isLazy?: boolean;
  isLoading?: boolean;
  className?: string;
  is3xDprEnabled?: boolean;
  highPriority?: boolean;
  dataTestId?: string;
  showPlaceholder?: boolean;
}

const Image = styled('img', getForwardPropOpts())<
  BorderColorProps &
    BorderWidthProps &
    BackgroundColorProps &
    BoxSizingProps &
    ObjectFitProps &
    DisplayProps
>`
  ${display}
  ${borderColor};
  ${borderWidth};
  ${borderRadius};
  ${styledBackgroundColor};
  ${boxSizing};
  ${objectFit};

  &:focus {
    border: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
  }
`;

const imageStyles: ComponentProps<typeof Image> = {
  boxSizing: 'border-box',
  objectFit: 'contain',
};

const buildSrcSet = (
  format: ImageFormat,
  originalUrl: string,
  width: number,
  maxDpr: number
  // eslint-disable-next-line max-params
) => {
  const result = [];

  for (let dpr = 1; dpr <= maxDpr; dpr++) {
    const roundedWidth = getRoundedSizeBy50px(width * dpr);
    const url = transformImageUrl(originalUrl, {
      format: format.toLowerCase(),
      width: roundedWidth,
    });

    result.push(`${url} ${dpr}x`);
  }

  return result.join(',');
};

const maxWidthQuery = (width: number) => `(max-width: ${width}px)`;

const minWidthQuery = (width: number) => `(min-width: ${width}px)`;

const breakpointSizes = breakpoints.map(b => parseInt(b.slice(0, -2), 10));

const buildSourcesWithQueries = (ranges: number[][], widths: ResponsiveImageBreakpointWidths) =>
  [ImageFormat.AVIF, ImageFormat.WEBP].reduce(
    (
      accumulator: Array<{
        width: number;
        format: ImageFormat;
        mediaQuery: string;
      }>,
      format
    ) => [
      ...accumulator,
      ...ranges.map(range => {
        const [from, to] = range;
        const mediaQuery =
          to === 3
            ? minWidthQuery(breakpointSizes[from - 1])
            : maxWidthQuery(breakpointSizes[to] - 1);

        return {
          width: widths[to],
          format,
          mediaQuery,
        };
      }),
    ],
    []
  );

const buildSourcesWithoutQueries = (width: ResponsiveImageSingleWidth) =>
  [ImageFormat.AVIF, ImageFormat.WEBP].map(format => ({ width, format }));

const getRoundedSizeBy50px = (size: number) => {
  if (size <= 50) {
    return 50;
  }

  if (size <= 200) {
    return Math.ceil(size / 50) * 50;
  }

  if (size >= 1000) {
    return Math.floor(size / 50) * 50;
  }

  return Math.round(size / 50) * 50;
};

const areSizesEqualForAllDensities = (
  currentSize: number,
  previousSize: number,
  maxDpr: number
) => {
  for (let dpr = 1; dpr <= maxDpr; dpr++) {
    if (getRoundedSizeBy50px(currentSize * dpr) !== getRoundedSizeBy50px(previousSize * dpr)) {
      return false;
    }
  }

  return true;
};

const getWidthRanges = (maxDpr: number, widths: ResponsiveImageBreakpointWidths) => {
  let start = 0;
  let end = 0;

  const ranges = [];

  for (let i = 1; i < widths.length; i++) {
    if (areSizesEqualForAllDensities(widths[i], widths[i - 1], maxDpr)) {
      end = i;
    } else {
      ranges.push([start, end]);

      start = i;
      end = i;
    }
  }

  ranges.push([start, end]);

  return ranges;
};

const isSingleWidth = (widths: ResponsiveImageWidths): widths is ResponsiveImageSingleWidth =>
  !Array.isArray(widths);

const buildSources = (
  maxDpr: number,
  widths: ResponsiveImageSingleWidth | ResponsiveImageBreakpointWidths
): Array<{
  width: number;
  mediaQuery?: string;
  format: ImageFormat;
}> => {
  if (!isSingleWidth(widths)) {
    const ranges = getWidthRanges(maxDpr, widths);
    if (ranges.length === 1) {
      return buildSourcesWithoutQueries(widths[0]);
    }

    return buildSourcesWithQueries(ranges, widths);
  }

  return buildSourcesWithoutQueries(widths);
};

interface ConditionalPropsInput {
  alt: string | undefined;
  isLoading: boolean;
  highPriority: boolean | undefined;
  backgroundColor: string | undefined;
  dataTestId: string | undefined;
}

const getConditionalProps = ({
  alt,
  isLoading,
  highPriority,
  backgroundColor,
  dataTestId,
}: ConditionalPropsInput) => ({
  ...(alt ? { 'aria-live': 'polite' as const, 'aria-busy': isLoading } : {}),
  ...(highPriority ? { fetchPriority: 'high' as const } : {}),
  ...(backgroundColor
    ? { backgroundColor: backgroundColor as BackgroundColorProps['backgroundColor'] }
    : {}),
  ...(dataTestId ? { 'data-testid': dataTestId } : {}),
});

const getPicture = ({ src, widths, fallbackSize, is3xDprEnabled }) => {
  const maxDpr = is3xDprEnabled ? 3 : 2;
  const sources: Array<React.SourceHTMLAttributes<HTMLSourceElement>> = buildSources(
    maxDpr,
    widths
  ).map(source => ({
    width: source.width,
    height: source.width,
    media: source.mediaQuery,
    srcSet: buildSrcSet(source.format, src, source.width, maxDpr),
    type: `image/${source.format.toLowerCase()}`,
  }));

  return {
    fallback: transformImageUrl(src, {
      width: getRoundedSizeBy50px(fallbackSize),
      format: ImageFormat.JPG,
    }),
    sources,
  };
};

const ResponsiveImage = ({
  src,
  widths,
  fallbackSize = Array.isArray(widths) ? widths[widths.length - 1] : widths,
  alt,
  isLazy = false,
  isLoading = false,
  className,
  onClick,
  onTouchStart,
  is3xDprEnabled,
  onLoad,
  highPriority,
  showPlaceholder = true,
  dataTestId,
  ...props
}: ResponsiveImageProps) => {
  const imageSrc = src || FALLBACK_IMAGE_URL;

  const imgRef = useRef<HTMLImageElement>(null);
  const { fallback, sources } = useMemo(
    () =>
      getPicture({
        src: imageSrc,
        widths,
        fallbackSize,
        is3xDprEnabled,
      }),
    [imageSrc, widths, fallbackSize, is3xDprEnabled]
  );

  const { backgroundColor } = useLoadingImageBackground(!showPlaceholder || !isLazy, imgRef);

  const conditionalProps = getConditionalProps({
    alt,
    isLoading,
    highPriority,
    backgroundColor,
    dataTestId,
  });

  return (
    <picture>
      {sources.map((source, idx) => (
        <source key={idx} {...source} />
      ))}
      <Image
        {...props}
        {...imageStyles}
        {...createLazyLoadingAttribute(isLazy)}
        ref={imgRef}
        src={fallback}
        alt={alt || ''}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onLoad={onLoad}
        className={className}
        width={fallbackSize}
        height={fallbackSize}
        {...conditionalProps}
      />
    </picture>
  );
};

export default ResponsiveImage;
